import { useUserProfile } from '@/context/UserProfileContext';
import { cn } from '@/lib/utils';
import { IonContent, IonInput, IonModal } from '@ionic/react';
import { useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';

import { useMediaUpload } from '@/hooks/post/useMediaUpload';
import { usePostForm } from '@/hooks/post/usePostForm';
import { useSharedMedia } from '@/hooks/post/useSharedMedia';

import { AudienceSelector } from './AudienceSelector';
import { LocationSelector } from './LocationSelector';
import { MediaGallery } from './MediaGallery';
import { MediaUploadButtons } from './MediaUploadButtons';
import { PostFormFields } from './PostFormFields';
import { PostModalHeader } from './PostModalHeader';
import { PublishingOverlay } from './PublishingOverlay';

type CreatePostModalProps = {
  addPostToFeed?: (post: any) => void;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  sharedMedia?: Array<{
    type: string;
    path: string;
    value?: string;
    mimeType?: string;
    fileContent?: string;
  }>;
};

export function CreatePostModal({
  addPostToFeed,
  isOpen,
  onOpenChange,
  sharedMedia = []
}: CreatePostModalProps) {
  const modal = useRef<HTMLIonModalElement>(null);
  // Modal reference for controlling the modal programmatically
  // Translation hook used in child components
  const { profile } = useUserProfile();

  // State for view management
  const [view, setView] = useState<'main' | 'location' | 'players' | 'preview'>('main');
  const [audienceModalOpen, setAudienceModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Initialize hooks
  const {
    form,
    loading,
    published,
    setDetectedHashtags,
    extractHashtags,
    handleHashtagSelect,
    resetForm,
    onSubmit
  } = usePostForm({
    addPostToFeed,
    onOpenChange
  });

  const {
    media,
    mediaDimensions,
    uppy,
    handleImageLoad,
    handleImageSelect,
    handleVideoSelect,
    handleDeleteMedia,
    resetMedia,
    setMedia
  } = useMediaUpload();

  // Process shared media
  useSharedMedia({
    isOpen,
    sharedMedia,
    uppy,
    userProfileId: profile?.id,
    setMedia
  });

  // Handle modal open/close
  const handleOpenChange = (open: boolean) => {
    onOpenChange(open);
    if (!open) {
      resetForm();
      resetMedia();
      setView('main');
      setSearchTerm('');
    }
  };

  // Handle caption changes to extract hashtags
  const handleCaptionChange = (value: string) => {
    // Extract hashtags when typing
    const extractedTags = extractHashtags(value);
    if (extractedTags.length > 0) {
      setDetectedHashtags(prev => {
        const newTags = [...prev];
        extractedTags.forEach(tag => {
          if (!newTags.includes(tag)) {
            newTags.push(tag);
          }
        });
        return newTags;
      });
    }
  };

  // Handle form submission
  const handleSubmit = () => {
    form.handleSubmit((data) => onSubmit(data, media))();
  };

  if (!profile) {
    return null;
  }

  // Only render the modal content when it's open to prevent form context issues
  if (!isOpen) {
    return null;
  }

  return (
    <FormProvider {...form}>
      <IonModal
        ref={modal}
        isOpen={isOpen}
        onDidDismiss={() => handleOpenChange(false)}
      >
        <PostModalHeader
          form={form}
          onClose={() => handleOpenChange(false)}
          onPublish={handleSubmit}
          media={media}
        />

        <IonContent color="light" className="ion-padding hide-scrollbar" scrollY={true} scrollEvents={true}>
          {(loading || published) && (
            <PublishingOverlay isLoading={loading} isPublished={published} />
          )}

          <div className="relative size-full overflow-x-hidden overflow-y-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <div
              className={cn(
                'absolute inset-0 transition-transform duration-300 ease-in-out',
                view === 'main' ? 'translate-x-0' : '-translate-x-full',
              )}
            >
              <form onSubmit={form.handleSubmit((data) => onSubmit(data, media))}>
                <MediaGallery
                  media={media}
                  mediaDimensions={mediaDimensions}
                  onImageLoad={handleImageLoad}
                  onDelete={handleDeleteMedia}
                />

                <input
                  type="tel"
                  size={5}
                  tabIndex={-1}
                  readOnly
                  className="hidden"
                />

                <PostFormFields
                  form={form}
                  onCaptionChange={handleCaptionChange}
                  onHashtagSelect={handleHashtagSelect}
                  onAudienceClick={() => setAudienceModalOpen(true)}
                />

                <MediaUploadButtons
                  mediaCount={media.length}
                  onImageSelect={handleImageSelect}
                  onVideoSelect={handleVideoSelect}
                  onLocationSelect={() => setView('location')}
                />

                <LocationSelector form={form} />
              </form>
            </div>
          </div>
        </IonContent>

        <AudienceSelector
          form={form}
          isOpen={audienceModalOpen}
          onClose={() => setAudienceModalOpen(false)}
        />
      </IonModal>
    </FormProvider>
  );
}
